<template>
  <div class="p-field">
    <div>
      <label v-if="label" :for="id" class="label-text flex justify-between" :class="{ 'p-error': errorMessage }">
       <div class="flex">
        <p>{{ label }}</p>
        <span v-if="required" class="text-red-500">*</span>
       </div>


        <slot name="adddata"></slot>
      </label>
    </div>
    <div>
      <!-- {{fieldValue}} -->
      <Select class="w-full" :id="id" v-model="fieldValue" :options="safeOptions" :optionLabel="optionLabel"
        :optionValue="optionValue" :optionDisabled="optionDisabled" :optionGroupLabel="optionGroupLabel"
        :optionGroupChildren="optionGroupChildren" :placeholder="placeholder" :disabled="disabled" :filter="filter"
        :filterPlaceholder="filterPlaceholder" :filterLocale="filterLocale" :filterMatchMode="filterMatchMode"
        :filterFields="computedFilterFields" :editable="editable" :showClear="showClear" :panelClass="panelClass"
        :scrollHeight="scrollHeight" :virtualScrollerOptions="virtualScrollerOptions" :autoOptionFocus="autoOptionFocus"
        :selectOnFocus="selectOnFocus" :focusOnHover="focusOnHover" :autoFilterFocus="autoFilterFocus"
        :resetFilterOnHide="resetFilterOnHide" :class="{ 'p-invalid': isInvalid }" :pt="{
          root: { class: `${wrapperClass} ${size === 'small' ? 'p-select-sm' : ''}` }
        }" :aria-label="label" :aria-describedby="ariaDescribedBy" :aria-invalid="!!isInvalid" :required="required"
        @change="handleChange" @filter="$emit('filter', $event)" @before-show="$emit('before-show')"
        @before-hide="$emit('before-hide')" @show="$emit('show')" @hide="$emit('hide')"
        @focus="onFocus" @blur="onBlur">
        <template v-if="$slots.value" #value="slotProps">
          <slot name="value" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.option" #option="slotProps">
          <slot name="option" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.optiongroup" #optiongroup="slotProps">
          <slot name="optiongroup" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.emptyfilter" #emptyfilter>
          <slot name="emptyfilter"></slot>
        </template>

        <template v-if="$slots.empty" #empty>
          <slot name="empty"></slot>
        </template>

        <template v-if="$slots.header" #header>
          <slot name="header"></slot>
        </template>

        <template v-if="$slots.footer" #footer>
          <slot name="footer"></slot>
        </template>

        <template v-if="$slots.loader" #loader>
          <slot name="loader"></slot>
        </template>
      </Select>
    </div>
    <div>
      <small v-if="helperText" :id="helperTextId" class="p-hint" :class="{ 'p-error': isInvalid }">{{ helperText }}</small>
      <small v-if="shouldShowError" :id="`${id}-error`" class="p-error block text-red-500">
        {{ getErrorMessage }}
      </small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed,watch } from 'vue'
import Select from 'primevue/select'
import type { VirtualScrollerProps } from 'primevue/virtualscroller'
import type { SelectChangeEvent } from 'primevue/select'
import { useVeeValidate } from '../../../../composables/useVeeValidate'

interface Props {
  modelValue?: any
  options?: any[]
  optionLabel?: string
  optionValue?: string
  optionDisabled?: string
  optionGroupLabel?: string
  optionGroupChildren?: string
  placeholder?: string
  disabled?: boolean
  filter?: boolean
  filterPlaceholder?: string
  filterLocale?: string
  filterMatchMode?: string
  filterFields?: string[]
  editable?: boolean
  showClear?: boolean
  panelClass?: string
  scrollHeight?: string
  virtualScrollerOptions?: VirtualScrollerProps
  autoOptionFocus?: boolean
  selectOnFocus?: boolean
  focusOnHover?: boolean
  autoFilterFocus?: boolean
  resetFilterOnHide?: boolean
  wrapperClass?: string
  label?: string
  helperText?: string
  id?: string
  size?: 'small' | 'normal'
  required?: boolean
  name: string
  validationType?: 'email' | 'phone' | 'password' | 'url' | 'numeric' | 'alpha' | 'alphanumeric'
  customRules?: Record<string, (value: any) => boolean | string>
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  options: () => [],
  optionLabel: undefined,
  optionValue: undefined,
  optionDisabled: undefined,
  optionGroupLabel: undefined,
  optionGroupChildren: undefined,
  placeholder: undefined,
  disabled: false,
  filter: true,
  filterPlaceholder: undefined,
  filterLocale: undefined,
  filterMatchMode: 'contains',
  filterFields: () => [],
  editable: false,
  showClear: false,
  panelClass: undefined,
  scrollHeight: '200px',
  virtualScrollerOptions: undefined,
  autoOptionFocus: true,
  selectOnFocus: false,
  focusOnHover: false,
  autoFilterFocus: false,
  resetFilterOnHide: false,
  wrapperClass: undefined,
  label: undefined,
  helperText: undefined,
  id: () => `one-dropdown-${Math.random().toString(36).substr(2, 9)}`,
  size: 'normal',
  required: false,
  validationType: undefined,
  customRules: undefined
})

// Ensure options is always an array
const safeOptions = computed(() => {
  return Array.isArray(props.options) ? props.options : []
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', event: SelectChangeEvent): void
  (e: 'filter', event: { originalEvent: Event; value: string }): void
  (e: 'before-show'): void
  (e: 'before-hide'): void
  (e: 'show'): void
  (e: 'hide'): void
  (e: 'focus', event: Event): void
  (e: 'blur', event: Event): void
}>()

// Local State
const isFocused = ref(false)
const touched = ref(false)

// Computed property for filter fields
const computedFilterFields = computed(() => {
  // If filterFields is provided and not empty, use it
  if (props.filterFields && props.filterFields.length > 0) {
    return props.filterFields;
  }

  // If optionLabel is provided, use it as the default filter field
  if (props.optionLabel) {
    return [props.optionLabel];
  }

  // Default to empty array if neither is provided
  return [];
});

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty
} = useVeeValidate({
  name: props.name,
  modelValue: props.modelValue,
  required: props.required,
  validationType: props.validationType,
  customRules: props.customRules
})

// Initialize fieldValue with modelValue
fieldValue.value = props.modelValue==0?undefined:props.modelValue

const isInvalid = computed(() => shouldShowError.value)

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  if (shouldShowError.value) ids.push(`${props.id}-error`)
  return ids.join(' ')
})

const getErrorMessage = computed(() => {
  if (props.required && isEmpty(fieldValue.value)) {
    return `${props.name} is required`
  }
  return errorMessage.value
})

const handleChange = async (event: SelectChangeEvent) => {
  updateValue(event.value)
  emit('update:modelValue', event.value)
  emit('change', event)

  if (shouldShowError.value) {
    await validate()
  }
}

const onFocus = (event: Event) => {
  isFocused.value = true
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  isFocused.value = false
  touched.value = true
  emit('blur', event)
  await handleValidation()
}

watch(
      () => props.modelValue,
      (newVal) => {
        fieldValue.value = newVal;
      }
    );

// Expose methods for parent form validation
defineExpose({
  validate,
  errorMessage,
  isValid: computed(() => !isInvalid.value)
})
</script>
